import { Trans } from 'react-i18next';
import i18next from 'i18next';
import { Divider, Modal, useEventCallback } from '@mui/material';
import { assertNever } from '@lmt/unicorn-shared/app/helpers/assert-never';
import CrossIcon from '@lmt/unicorn-shared/app/icons/cross.svg?react';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, ButtonColor } from '../buttons/Button';
import { ModalContextType, UseModalOptions } from './useModal.types';
import styles from '../../Dialog/Dialog.module.scss';
import { Field } from '../../Field';
import { Input } from '../inputs/Input';
import { createId } from '@lmt/unicorn-shared/app/helpers/create-id';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { IconButton } from '../IconButton';
//import styles from './CustomModal.module.scss';

const ModalContext = React.createContext<ModalContextType>({} as any);

export const useModal = (): ModalContextType => {
  const context = useContext(ModalContext);
  const idsRef = useRef(new Set<string>());
  // this is commented because of the bug # (modal is opened from a tooltip)
  // useEffect(() => {
  //  return () => {
  //    idsRef.current.forEach((x) => context.hide(x));
  //  };
  // }, []);
  return useMemo(() => {
    return {
      hide: context.hide,
      showAlert(options) {
        const result = context.showAlert({ ...options });
        idsRef.current.add(result.id);
        void result.then(() => idsRef.current.delete(result.id));
        return result;
      },
      showConfirm(options) {
        const result = context.showConfirm({ ...options });
        idsRef.current.add(result.id);
        void result.then(() => idsRef.current.delete(result.id));
        return result;
      },
      showDeletionConfirmation(options) {
        const result = context.showConfirm({
          ...options,
          okButtonColor: ButtonColor.SuperDanger,
          cancelButtonColor: ButtonColor.Primary,
        });
        idsRef.current.add(result.id);
        void result.then(() => idsRef.current.delete(result.id));
        return result;
      },
      showError(options) {
        const result = context.showError({ ...options });
        idsRef.current.add(result.id);
        void result.then(() => idsRef.current.delete(result.id));
        return result;
      },
      showPrompt(options) {
        const result = context.showPrompt({ ...options });
        idsRef.current.add(result.id);
        void result.then(() => idsRef.current.delete(result.id));
        return result;
      },
      showMultiButton(options) {
        const result = context.showMultiButton({ ...options });
        idsRef.current.add(result.id);
        void result.then(() => idsRef.current.delete(result.id));
        return result;
      },
      showCustom(options) {
        const result = context.showCustom({ ...options });
        idsRef.current.add(result.id);
        void result.then(() => idsRef.current.delete(result.id));
        return result;
      },
    };
  }, [context]);
};

export const ModalProvider: React.FC<React.PropsWithChildren> = (props) => {
  const [modals, setModals] = useState<UseModalOptions<any>[]>([]);
  const i18n = useScopedTranslation('uikit.dialog');
  const addModal = useCallback(
    (modal: UseModalOptions<any>, promise: Promise<unknown>) => {
      setModals((o) => [...o, modal]);
      void promise.finally(() => {
        // we use setTimeout to allow hiding form animations to finish
        setTimeout(
          () => setModals((o) => [...o.filter((x) => x.id !== modal.id)]),
          1000,
        );
      });
    },
    [],
  );
  const contextValue: ModalContextType = useMemo(() => {
    return {
      hide: (id: string) => {
        setModals((modals) => {
          return modals.filter((x) => x.id === id);
        });
      },
      showError: async (options) => {
        const id = createId();
        const promise = new Promise<void>((resolve, reject) => {
          // we use setTimout to be able to access the promise
          setTimeout(() =>
            addModal(
              {
                type: 'alert',
                title: i18n.t('error_title'),
                ...options,
                id,
                resolve: resolve,
              },
              promise,
            ),
          );
        });
        (promise as any).id = id;
        return promise;
      },

      showAlert: async (options) => {
        const id = createId();
        const promise = new Promise<void>((resolve, reject) => {
          // we use setTimout to be able to access the promise
          setTimeout(() =>
            addModal(
              {
                type: 'alert',
                title: i18n.t('alert_title'),
                ...options,
                id,
                resolve: resolve,
              },
              promise,
            ),
          );
        });
        (promise as any).id = id;
        return promise;
      },
      showConfirm: async (options) => {
        const id = createId();
        const promise = new Promise<boolean>((resolve, reject) => {
          // we use setTimout to be able to access the promise
          setTimeout(() =>
            addModal(
              {
                type: 'confirm',
                title: i18n.t('confirm_title'),
                ...options,
                id,
                resolve: resolve,
              },
              promise,
            ),
          );
        });
        (promise as any).id = id;
        return promise;
      },
      showPrompt: async (options) => {
        const id = createId();
        const promise = new Promise<string | null>((resolve, reject) => {
          // we use setTimout to be able to access the promise
          setTimeout(() =>
            addModal(
              {
                type: 'prompt',
                ...options,
                id,
                resolve: resolve,
              },
              promise,
            ),
          );
        });
        (promise as any).id = id;
        return promise;
      },
      showMultiButton: async (options) => {
        const id = createId();
        const promise = new Promise<string | null>((resolve, reject) => {
          // we use setTimout to be able to access the promise
          setTimeout(() =>
            addModal(
              {
                type: 'multibutton',
                ...options,
                id,
                resolve: resolve,
              },
              promise,
            ),
          );
        });
        (promise as any).id = id;
        return promise;
      },
      showCustom: async (options) => {
        const id = createId();
        const promise = new Promise<any | null>((resolve, reject) => {
          // we use setTimout to be able to access the promise
          setTimeout(() =>
            addModal(
              {
                type: 'custom',
                ...options,
                id,
                resolve: resolve,
              },
              promise,
            ),
          );
        });
        (promise as any).id = id;
        return promise;
      },
    } as ModalContextType;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addModal, i18n.i18n.language]);

  return (
    <ModalContext.Provider value={contextValue}>
      {props.children}
      {modals.map((x) => (
        <SingleModal key={x.id} options={x} />
      ))}
    </ModalContext.Provider>
  );
};

type SingleModalProps = { options: UseModalOptions };
export const SingleModal: React.FC<SingleModalProps> = (props) => {
  const options = props.options;
  const [isShown, setIsShown] = useState<boolean>(true);
  const [inputError, setInputError] = useState<string>('');
  const [promptValue, setPromptValue] = useState(
    options.type === 'prompt' ? options.defaultValue : '',
  );
  const [customValue, setCustomValue] = useState<any>(
    options.type === 'custom' ? (options.defaultValue ?? null) : null,
  );
  const [_, rerender] = useState(1);
  const commonClose = useCallback(() => {
    setIsShown(false);
  }, []);

  const onCancel = useCallback(() => {
    setIsShown(false);
    if (!options) return;

    const type = options.type;
    switch (type) {
      case 'alert':
        options.resolve();
        break;

      case 'confirm':
        options.resolve(false);
        break;

      case 'prompt':
      case 'multibutton':
      case 'custom':
        options.resolve(null);
        break;

      default:
        assertNever(type);
    }
  }, [options]);

  const onCloseCustom = useEventCallback((value: any) => {
    setIsShown(false);
    if (!options || options.type !== 'custom') return;
    options.resolve(value);
  });

  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(
    function focusInputWhenShown() {
      if (!isShown) return;
      inputRef.current?.focus();
      setTimeout(() => inputRef.current?.focus(), 100);
      setTimeout(() => inputRef.current?.focus(), 1000);
    },
    [isShown],
  );

  function requiredValidate(value: string): string | boolean {
    return value.trim().length === 0 ? i18next.t('errors.required') : true;
  }

  const validateAndResolve = useCallback(
    async (
      value: string,
      setValue: React.Dispatch<React.SetStateAction<string>>,
    ) => {
      if (
        !options ||
        (options.type !== 'prompt' && options.type !== 'custom')
      ) {
        setIsShown(false);
        return;
      }

      if (options.type === 'prompt' && options.isMandatory) {
        const requiredValidationResult = requiredValidate(value);

        if (typeof requiredValidationResult === 'string') {
          setInputError(requiredValidationResult);
          return;
        }
      }

      if (options.validate) {
        const validationResult = await options.validate(value);

        if (options.type === 'prompt') {
          if (validationResult === false) {
            setInputError(options.errorText || '');
            return;
          }
          if (typeof validationResult === 'string') {
            setInputError(validationResult);
            return;
          }
        }

        if (!validationResult) {
          setValue(value);
          rerender((x) => x + 1);
          return;
        }
      }

      options.resolve(value);
      setIsShown(false);
    },
    [options],
  );

  const handleButtonClick = useCallback(async () => {
    if (!options) return;

    const type = options.type;
    switch (type) {
      case 'alert':
        options.resolve();
        setIsShown(false);
        break;
      case 'confirm':
        options.resolve(true);
        setIsShown(false);
        break;
      case 'prompt':
        await validateAndResolve(promptValue, setPromptValue);
        break;
      case 'custom':
        await validateAndResolve(customValue, setCustomValue);
        break;
      case 'multibutton':
        setIsShown(false);
        break;
      default:
        assertNever(type);
    }
  }, [options, validateAndResolve, promptValue, customValue]);

  return (
    <Modal
      className={styles.modal}
      open={isShown}
      title={options?.title ?? ''}
      onClose={onCancel}
      style={modalChildrenStyle}
      // stopPropagation is needed to not close tooltips.
      // e.g. Calendar Month View: create more than 12 visits per day, click on "2 more", then click on Cancel/Complete in Tooltip.
      // The Modal is shown, and if I click close/cancel/ok (or just select some text within a modal) we shouldn't close the Tooltip
      //
      // On the other hand it interferes with DropDowns inside the modal: i.e. they could not be closed by click outside (e.g. Bug #52626)
      // onMouseDown={stopPropagation}
    >
      <>
        {!options ? null : (
          <div className={styles.container} data-test-id={'dialog-container'}>
            <div className={styles.header}>
              <div className={styles.title} data-test-id="dialog-title">
                {options.title}
              </div>
              <IconButton
                color="light"
                className={styles.closeButton}
                iconComponent={<CrossIcon className={styles.crossIcon} />}
                onClick={onCancel}
                testId={'dialog-closeIcon'}
              />
            </div>
            <div className={styles.content}>
              {options.type === 'custom' ? (
                <options.Component
                  value={customValue}
                  setValue={setCustomValue}
                  onClose={onCloseCustom}
                />
              ) : (
                <div className={styles.text} data-test-id="dialog-text">
                  {options.allowStyleTagsInText ? (
                    <Trans
                      components={{
                        italic: <i />,
                        bold: <strong />,
                        danger: <DangerText />,
                        br: <br />,
                      }}
                    >
                      {options.text}
                    </Trans>
                  ) : (
                    options.text
                  )}
                </div>
              )}
              {options.type === 'prompt' ? (
                <div data-test-id="field-name">
                  <Field
                    title={options.fieldName ?? ''}
                    titleClassName={options.className}
                  >
                    <Input
                      autoFocus={true}
                      ref={inputRef}
                      value={promptValue}
                      maxLength={options.maxLength}
                      placeholder={options.placeholder}
                      onKeyDown={async (event) => {
                        if (event.key === 'Enter') {
                          await validateAndResolve(promptValue, setPromptValue);
                        }
                      }}
                      errorText={inputError}
                      onChange={(e) => {
                        setPromptValue(e.target.value);
                        if (options.validate?.(e.target.value)) {
                          setInputError('');
                        } else {
                          setInputError(options.errorText || '');
                        }
                      }}
                    />
                  </Field>
                </div>
              ) : null}
            </div>

            <Divider className={styles.divider} />
            <div className={styles.controlsContainer}>
              {options.type === 'custom' && options.Controls ? (
                <options.Controls
                  value={customValue}
                  setValue={setCustomValue}
                  onClose={onCloseCustom}
                />
              ) : (
                <div className={styles.buttonContainer}>
                  {options.type === 'multibutton' ? (
                    options.buttons.map((x, index) => (
                      <Button
                        key={x.id}
                        autoFocus={index === options.buttons.length - 1}
                        color={x.color ?? ButtonColor.Default}
                        className={styles.multibutton}
                        title={x.text}
                        onClick={() => {
                          options.resolve(x.id);
                          onCancel();
                        }}
                      />
                    ))
                  ) : (
                    <>
                      {options.type === 'confirm' ||
                      options.type === 'prompt' ||
                      options.type === 'custom' ? (
                        <Button
                          testId="dialog-cancelButton"
                          color={
                            options.cancelButtonColor ?? ButtonColor.Primary
                          }
                          title={
                            options.cancelButtonText ??
                            i18next.t('uikit.dialog.cancel_button')
                          }
                          onClick={onCancel}
                        />
                      ) : null}
                      <Button
                        color={options.okButtonColor ?? ButtonColor.Default}
                        type={'submit'}
                        autoFocus={true}
                        testId="dialog-confirmButton"
                        title={
                          options.okButtonText ??
                          i18next.t('uikit.dialog.ok_button')
                        }
                        onClick={handleButtonClick}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </>
    </Modal>
  );
};

const modalChildrenStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const DangerText: React.FC<React.PropsWithChildren> = (props) => {
  return <span className={styles.dangerText}>{props.children}</span>;
};

function isPromise(value: any) {
  return Boolean(value && typeof value.then === 'function');
}
